import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { AlertTriangle, CheckCircle2, HeartPulse, Send, Loader2, Zap } from "lucide-react";

const Row = ({ ok, label, detail, testId }) => (
  <div data-testid={testId} className={`flex items-start gap-3 rounded-xl px-4 py-3 border ${ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-300"}`}>
    {ok
      ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
      : <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />}
    <div className="min-w-0">
      <div className={`text-sm font-semibold ${ok ? "text-emerald-800" : "text-red-800"}`}>{label}</div>
      <p className={`text-xs mt-0.5 ${ok ? "text-emerald-700/80" : "text-red-700/90"}`}>{detail}</p>
    </div>
  </div>
);

const SystemHealthCard = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api.get("/admin/system-health")
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Could not load system health"));
  }, []);

  const sendTestEmail = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/admin/system-health/test-email");
      setTestResult({ ok: !!r.data?.sent, to: r.data?.to });
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Send failed" });
    } finally {
      setTesting(false);
      setTimeout(() => setTestResult(null), 8000);
    }
  };

  const [pinging, setPinging] = useState(false);
  const [webhookResult, setWebhookResult] = useState(null);
  const testWebhook = async () => {
    setPinging(true); setWebhookResult(null);
    try {
      const r = await api.post("/admin/webhook/test");
      setWebhookResult(r.data);
    } catch (e) {
      setWebhookResult({ error: e?.response?.data?.detail || "Webhook test failed" });
    } finally {
      setPinging(false);
      setTimeout(() => setWebhookResult(null), 12000);
    }
  };

  if (err) return <div className="bg-white rounded-2xl soft-shadow p-6 text-sm text-red-600">{err}</div>;
  if (!data) return null;

  const stripeOk = data.stripe?.livemode && data.stripe?.webhook_secret_set;
  const emailOk = !!data.email?.configured;

  return (
    <div data-testid="admin-system-health" className="bg-white rounded-2xl soft-shadow p-6 space-y-3">
      <div className="flex items-center gap-2">
        <HeartPulse className="w-5 h-5 text-coral-400" />
        <h2 className="font-semibold text-navy-700 text-lg">System health</h2>
      </div>
      <p className="text-sm text-navy-700/60 -mt-1">
        Live-only sanity checks. Every red row silently breaks a feature until you fix it in <code className="text-xs bg-cream-100 px-1.5 py-0.5 rounded">backend/.env</code>.
      </p>

      <Row
        ok={data.stripe?.livemode}
        label={data.stripe?.livemode ? "Stripe is in LIVE mode" : "Stripe is in TEST mode — real cards will NOT be charged"}
        detail={data.stripe?.livemode
          ? "Real charges will succeed. sk_live_ key detected."
          : "The STRIPE_API_KEY on this deployment is a test key. Update it to sk_live_... in your production env config, then redeploy."}
        testId="health-stripe-livemode"
      />

      <Row
        ok={!!data.stripe?.webhook_secret_set}
        label={data.stripe?.webhook_secret_set ? "Stripe webhook signing secret set" : "Missing Stripe webhook signing secret"}
        detail={data.stripe?.webhook_secret_set
          ? "checkout.session.completed events will be verified server-side."
          : "STRIPE_WEBHOOK_SECRET is empty. Register your webhook in the Stripe dashboard and paste the whsec_... value."}
        testId="health-stripe-webhook"
      />

      <Row
        ok={emailOk}
        label={emailOk ? "SMTP email delivery is configured" : "SMTP email delivery is NOT configured — no emails are being sent"}
        detail={emailOk
          ? "Welcome emails, event reminders, and paid-event confirmations will attempt SMTP delivery."
          : "SMTP_HOST and/or SMTP_USER are empty in backend/.env. Users and admins receive no emails until you add credentials (Resend / SendGrid / custom SMTP)."}
        testId="health-email-configured"
      />

      {emailOk && (
        <div className="flex items-center gap-3">
          <button
            onClick={sendTestEmail}
            disabled={testing}
            data-testid="health-send-test-email-btn"
            className="inline-flex items-center gap-2 bg-navy-700 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-navy-800 disabled:opacity-60"
          >
            {testing ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send test email to me</>}
          </button>
          {testResult && (
            <span
              data-testid="health-send-test-email-result"
              className={`text-xs font-semibold ${testResult.ok ? "text-emerald-700" : "text-red-700"}`}
            >
              {testResult.ok
                ? `Sent to ${testResult.to} — check your inbox (and spam).`
                : `Failed: ${testResult.error || "unknown"}`}
            </span>
          )}
        </div>
      )}

      {!emailOk && (
        <details className="text-xs text-navy-700/70 bg-cream-100 rounded-lg px-3 py-2" data-testid="health-email-help">
          <summary className="cursor-pointer font-semibold">How to enable email in 3 minutes (Resend)</summary>
          <ol className="mt-2 space-y-1 list-decimal list-inside">
            <li>Sign up at <a href="https://resend.com" target="_blank" rel="noreferrer" className="underline">resend.com</a> (free 3k/mo).</li>
            <li>Add your domain, verify DNS (SPF / DKIM / DMARC).</li>
            <li>API Keys → Create API Key → copy <code>re_...</code></li>
            <li>Set in backend/.env:
              <pre className="mt-1 bg-white rounded px-2 py-1 text-[11px] overflow-x-auto">SMTP_HOST=smtp.resend.com
SMTP_PORT=465
SMTP_USER=resend
SMTP_PASSWORD=re_your_api_key
SMTP_FROM_EMAIL=noreply@galeriecop.com
SMTP_FROM_NAME=GalerieCop</pre>
            </li>
            <li>Redeploy — welcome emails start flowing immediately.</li>
          </ol>
        </details>
      )}

      <Row
        ok={!!data.app?.public_url_set}
        label={data.app?.public_url_set ? `App public URL: ${data.app.public_url}` : "App public URL not configured"}
        detail={data.app?.public_url_set
          ? "Outbound email links use this URL as the base."
          : "APP_PUBLIC_URL is empty. Emails will fall back to the request host, which may be your preview subdomain."}
        testId="health-app-url"
      />

      {/* Slack / Discord webhook — real-time alerts for urgent enquiries */}
      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-cream-200">
        <button
          onClick={testWebhook}
          disabled={pinging}
          data-testid="health-test-webhook-btn"
          className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-purple-700 disabled:opacity-60"
        >
          {pinging ? <><Loader2 className="w-4 h-4 animate-spin" /> Pinging…</> : <><Zap className="w-4 h-4" /> Test Slack/Discord webhook</>}
        </button>
        {webhookResult && (
          <span
            data-testid="health-webhook-result"
            className="text-xs font-semibold text-navy-700/80"
          >
            {webhookResult.error
              ? <span className="text-red-700">Failed: {webhookResult.error}</span>
              : (!webhookResult.slack_configured && !webhookResult.discord_configured)
                ? <span className="text-amber-700">Neither webhook is configured — set SLACK_WEBHOOK_URL and/or DISCORD_WEBHOOK_URL in your deployment env vars.</span>
                : (
                  <span className="text-emerald-700">
                    {webhookResult.slack_configured && `Slack: ${webhookResult.sent?.slack ? "✅ sent" : "❌ failed"}`}
                    {webhookResult.slack_configured && webhookResult.discord_configured && " · "}
                    {webhookResult.discord_configured && `Discord: ${webhookResult.sent?.discord ? "✅ sent" : "❌ failed"}`}
                  </span>
                )}
          </span>
        )}
      </div>
    </div>
  );
};

export default SystemHealthCard;
